import classNames from "classnames";
import React from "react";

import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { isNil } from "lodash";
import { DateTime } from "luxon";

type Props = {
  onChange: (date: DateTime) => void;
  date: DateTime | null;
  readOnly: boolean;
  error: string | null;
};

export const ECDatePicker = ({ onChange, date: _date, readOnly, error }: Props) => {
  const [date, setDate] = React.useState<Date | null>(_date?.toJSDate() ?? null);

  React.useEffect(() => {
    if (_date) {
      setDate(_date.toJSDate());
    }
  }, [_date]);

  const handleNavigateToDate = (dateMoment: MaterialUiPickersDate) => {
    if (!dateMoment) {
      return;
    }

    setDate(DateTime.fromISO(dateMoment.toISOString()).toJSDate());
    onChange(DateTime.fromISO(dateMoment.toISOString(), { zone: getRenderTimeZone() }));
  };

  const placeholder = isNil(date) ? "Choose date" : undefined;

  return (
    <DatePicker
      placeholder={placeholder}
      id="day-date-picker"
      onChange={handleNavigateToDate}
      value={date}
      variant="inline"
      autoOk
      disableToolbar
      disabled={readOnly}
      format={"ddd, MMMM DD YYYY"}
      className={classNames(
        "cw-text-13 cw-font-medium",
        "cw-box-border",
        "cw-px-2 cw-h-7 cw-flex-1",
        "cw-border cw-border-solid",
        "cw-font-body",
        "cw-rounded-lg",
        "cw-transition",
        "cw-outline-1",
        "placeholder:cw-text-subtle",
        "cw-bg-default focus:cw-border-brand-interactable focus:hover:cw-border-brand-interactable-hover focus-visible:cw-outline-brand-interactable focus:cw-outline-brand-interactable focus:hover:cw-outline-brand-interactable-hover",
        "cw-justify-center",
        {
          "cw-border-muted hover:cw-border-muted-hover cw-text-default cw-cursor-pointer": !readOnly,
          "cw-text-default-disabled cw-border-subtle cw-cursor-not-allowed": readOnly,
          "cw-bg-destructive cw-outline cw-border-destructive-interactable cw-outline-destructive-interactable hover:cw-border-destructive-interactable-hover hover:cw-outline-destructive-interactable-hover focus-visible:cw-outline-destructive-interactable-pressed focus:cw-outline-destructive-interactable-pressed": error,
        },
      )}
      InputProps={{
        disableUnderline: true,
        classes: {
          root: readOnly ? "cw-cursor-not-allowed" : "cw-cursor-pointer",
          input: `cw-font-medium cw-text-13 ${
            readOnly
              ? "cw-cursor-not-allowed cw-text-default-disabled"
              : "cw-cursor-pointer cw-text-default"
          }`,
        },
        label: "Select Month",
        tabIndex: -1,
        role: "presentation",
      }}
    />
  );
};
